import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { RouterModule } from '@angular/router';
import { HomeStateService } from '@app/core/services/home-state.service';
import { MaterialModule } from '@app/material.module';
import { ThemeService } from '@app/theme.service';
import { AuthService } from '@core/services/auth.service';
import { ImageOptimizationService } from '@core/services/image-optimization.service';
import { NotificationService } from '@core/services/notification.service';
import { RcService } from '@core/services/rc.service';
import { ProfileComponent } from '@shared/profile/profile.component';
import moment from 'moment';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';

@Component({
	selector: 'app-header',
	templateUrl: './header.component.html',
	styleUrls: ['./header.component.css'],
	standalone: true,
	imports: [CommonModule, RouterModule, MaterialModule]
})
export class HeaderComponent implements OnInit, OnDestroy {
	private userSubscription!: Subscription;
	private notificationSubscription!: Subscription;
	private timeCardSettingsSubscription!: Subscription;
	currentUser: any;
	logo: string = '/assets/images/logo.webp';
	fabLogoSm: string = '/assets/images/fav-icon.png';
	currentDateTime: string = '';
	employeeNumber: number = 0;
	userImage: string | null = '/assets/images/user-192x192.png';
	notifications: any;
	isSupervisor: any;
	isReviewer: any;

	isDarkTheme: boolean = false;
	notificationsShown: boolean = false;

	/** Server's wall-clock time at the moment the API response was received */
	private serverBaseTime: moment.Moment | null = null;
	/** Local Date.now() captured at the same instant as serverBaseTime */
	private serverBaseLocalMs: number = 0;

	constructor(
		private readonly themeService: ThemeService,
		public readonly rcService: RcService,
		private readonly authService: AuthService,
		private readonly dialogRef: MatDialog,
		private readonly imageOptimizationService: ImageOptimizationService,
		private readonly notificationService: NotificationService,
		private readonly cdr: ChangeDetectorRef,
		private readonly homeStateService: HomeStateService
	) {
		// Initialize logo with immediate WebP assumption to use preloaded resource
		this.logo = this.imageOptimizationService.getOptimizedLogoUrlSync();
	}

	ngOnInit() {
		// Optimize logo URL once WebP detection is complete
		this.optimizeLogo();

		this.themeService.isDarkTheme$.subscribe((isDarkTheme) => {
			this.isDarkTheme = isDarkTheme;
			document.body.classList.toggle('dark', isDarkTheme);
		});

		this.updateTimer(); // Initial update

		// Update the timer every second
		setInterval(() => {
			this.updateTimer();
		}, 1000);

		// Update the timer every second
		setInterval(() => {
			this.updateTimer();
		}, 1000);

		// Subscribe to server time from getTimeCardSettings (fetched on home page load).
		// We treat CurrentDateTime as the server's wall-clock time (no TZ assumptions).
		// We capture the local Date.now() at the same instant so we can tick forward accurately.
		this.timeCardSettingsSubscription =
			this.homeStateService.timeCardSettings$
				.pipe(filter((settings) => !!settings?.CurrentDateTime))
				.subscribe((settings) => {
					// Parse server time as-is (no timezone conversion)
					this.serverBaseTime = moment(settings.CurrentDateTime);
					// Record the local clock at this exact moment
					this.serverBaseLocalMs = Date.now();
					// Immediately reflect the server time in the header
					this.updateTimer();
				});

		this.userSubscription = this.authService.currentUser$
			.pipe(
				distinctUntilChanged((prev, curr) => {
					// Compare employee numbers to detect user changes
					const prevEmployeeNumber =
						prev?.credentials?.EmployeeNumber;
					const currEmployeeNumber =
						curr?.credentials?.EmployeeNumber;
					return prevEmployeeNumber === currEmployeeNumber;
				})
			)
			.subscribe((user) => {
				const previousEmployeeNumber =
					this.currentUser?.credentials?.EmployeeNumber;
				const newEmployeeNumber = user?.credentials?.EmployeeNumber;

				// Check if this is a different user
				const isDifferentUser =
					previousEmployeeNumber &&
					previousEmployeeNumber !== newEmployeeNumber;

				if (isDifferentUser) {
					// Clear previous user's image immediately when switching users
					this.userImage = '/assets/images/user-192x192.png';
				}

				this.currentUser = user;
				this.employeeNumber = user?.credentials?.EmployeeNumber;
				this.isSupervisor = user?.isSupervisor;
				this.isReviewer = user?.canReviewFirstLevel;
				this.updateUserImage();

				// Check for image again after a short delay in case it wasn't loaded yet
				setTimeout(() => {
					this.updateUserImage();
				}, 100);

				// this.notificationsShown =
				// 	user?.notificationsShown || !user?.ApplicationUserName;
			});

		// Listen to logout events to clear notifications
		this.authService.logoutEvent.subscribe(() => {
			this.notifications = [];
		});

		// Subscribe to notifications with better logging
		this.notificationSubscription = this.notificationService
			.getNotifications()
			.subscribe((notifications) => {
				this.notifications = notifications;
			});
	}

	toggleTheme() {
		this.themeService.toggleTheme();
	}

	updateTimer() {
		let currentDate: moment.Moment;
		if (this.serverBaseTime) {
			// Advance the server's baseline by however much real time has elapsed locally
			const elapsedMs = Date.now() - this.serverBaseLocalMs;
			currentDate = this.serverBaseTime
				.clone()
				.add(elapsedMs, 'milliseconds');
		} else {
			// Before the API responds, fall back to local browser time
			currentDate = moment();
		}
		this.currentDateTime = currentDate.format('MM/DD/YYYY hh:mm:ss A');
	}

	openProfileDailog() {
		const dialogRef = this.dialogRef.open(ProfileComponent, {
			width: '750px', // Set the desired width
			panelClass: 'custom-profile-setting',
			data: {
				// showEvents: true
			}
		});

		// Subscribe to dialog close event to trigger user data refresh
		dialogRef.afterClosed().subscribe(() => {
			// Force immediate user data refresh when profile dialog closes
			// This ensures all components (especially home page) get updated data
			setTimeout(() => {
				this.authService.forceEmitCurrentUser();
				this.cdr.detectChanges();
			}, 100);
		});
	}

	private async optimizeLogo() {
		// Optimize logo URL once WebP detection is complete
		this.logo = await this.imageOptimizationService.getOptimizedLogoUrl();
	}

	private updateUserImage() {
		const storedImage = localStorage.getItem('employeeImage');
		if (storedImage && storedImage !== 'data:image/png;base64,null') {
			this.userImage = storedImage;
		} else {
			this.userImage = '/assets/images/user-192x192.png';
		}
	}

	ngOnDestroy() {
		if (this.userSubscription) {
			this.userSubscription.unsubscribe();
		}
		if (this.notificationSubscription) {
			this.notificationSubscription.unsubscribe();
		}
		if (this.timeCardSettingsSubscription) {
			this.timeCardSettingsSubscription.unsubscribe();
		}
	}
}
